/**
 * Lag input parsing/formatting. P6 stores lag in hours; we display in workdays
 * by default. The input accepts:
 *   "0"                 → 0
 *   "2", "2d", "+2d"    → 16  (bare number defaults to days; workday = 8h)
 *   "-1d"               → -8
 *   "0.5d"              → 4
 *   "16h", "-8h"        → 16, -8  (explicit hours unit)
 *
 * Invalid input → null. Caller surfaces a visual error state.
 */

const HOURS_PER_DAY = 8;

export function parseLagInput(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) return 0;
  const match = trimmed.match(/^([+-]?(?:\d+(?:\.\d+)?|\.\d+))\s*([dh]?)$/i);
  if (!match) return null;
  const num = parseFloat(match[1]);
  if (!Number.isFinite(num)) return null;
  const unit = match[2].toLowerCase();
  if (unit === "h") return Math.round(num);
  return Math.round(num * HOURS_PER_DAY);
}

/** Format hours back to the display string the user prefers — workdays
 * if the value is a clean multiple of 8, hours otherwise. */
export function formatLagInput(lagHours: number): string {
  if (lagHours === 0) return "0";
  if (lagHours % HOURS_PER_DAY === 0) {
    const days = lagHours / HOURS_PER_DAY;
    return days > 0 ? `+${days}d` : `${days}d`;
  }
  // Fractional days — show with one decimal in days for readability
  const days = lagHours / HOURS_PER_DAY;
  if (Number.isFinite(days)) {
    const rounded = Math.round(days * 10) / 10;
    if (rounded * HOURS_PER_DAY === lagHours) {
      return rounded > 0 ? `+${rounded}d` : `${rounded}d`;
    }
  }
  // Non-clean fraction — fall back to hours
  return lagHours > 0 ? `+${lagHours}h` : `${lagHours}h`;
}
